import { useIsHydrated, useMobileMediaQuery } from '@iheartjane/reefer';
import type { ReactNode } from 'react';

interface ClientOnlyWithMediaQueryProps {
  children: (isMobile: boolean) => ReactNode;
}

const ClientOnlyWithMediaQuery = ({
  children,
}: ClientOnlyWithMediaQueryProps) => {
  const isMobile = useMobileMediaQuery({});
  return <>{children(isMobile)}</>;
};

interface ClientOnlyProps extends ClientOnlyWithMediaQueryProps {
  /**
   * You are encouraged to add a fallback that is the same dimensions
   * as the client rendered children to avoid content layout shift.
   */
  fallback?: ReactNode;
}

/**
 * Render the children only after the JS has loaded client-side. Use an optional
 * fallback component if the JS is not yet loaded.
 *
 * Example: Render a Chart component if JS loads, renders a simple FakeChart
 * component server-side or if there is no JS. The FakeChart can have only the
 * UI without the behavior or be a loading spinner or skeleton.
 * ```tsx
 * return (
 *   <ClientOnly fallback={<FakeChart />}>
 *     {() => <Chart />}
 *   </ClientOnly>
 * );
 * ```
 */
export const ClientOnly = ({ children, fallback = null }: ClientOnlyProps) => {
  return useIsHydrated() ? (
    <ClientOnlyWithMediaQuery>{children}</ClientOnlyWithMediaQuery>
  ) : (
    <>{fallback}</>
  );
};
